/**
 * INCENTIVE-TRACKER MODULE
 * Self-contained. Import only from /shared or within this module.
 *
 * Attachments over the private `incentive-attachments` bucket. Path =
 * <account_id>/<program_id>/<uuid>.<ext> so Storage RLS (migration 0041) gates
 * uploads/reads on account membership of the first path segment. The row in
 * incentive_attachments is the catalog; reads use short-lived signed URLs.
 */

import { supabase } from "../../../shared/utils/supabaseClient.js";
import { getMyPrimaryAccount } from "../../../shared/utils/accountService.js";

export const ATTACH_ACCEPT = [
  "image/jpeg", "image/png", "image/gif", "image/webp", "application/pdf",
  "text/plain", "text/csv", "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
export const ATTACH_MAX_MB = 10;

export function validateAttachment(file) {
  if (!ATTACH_ACCEPT.includes(file.type)) return "Unsupported file type.";
  if (file.size > ATTACH_MAX_MB * 1024 * 1024) return `Each file must be under ${ATTACH_MAX_MB} MB.`;
  return null;
}

function uid() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}

async function accountId() {
  const acct = await getMyPrimaryAccount();
  if (!acct?.accountId) throw new Error("No workspace found for your account.");
  return acct.accountId;
}

/**
 * Upload one file and catalog it. `kind` is 'program' (program-level doc) or
 * 'score_evidence' (an outside report backing a goal's actual); `goalId` links
 * it to a goal when relevant.
 */
export async function uploadAttachment(programId, file, { kind = "program", goalId = null } = {}) {
  const bad = validateAttachment(file);
  if (bad) throw new Error(bad);
  const acct = await accountId();
  const ext = (file.name.includes(".") ? file.name.split(".").pop() : "bin")
    .toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 8) || "bin";
  const path = `${acct}/${programId}/${uid()}.${ext}`;

  const { error: upErr } = await supabase.storage
    .from("incentive-attachments")
    .upload(path, file, { contentType: file.type });
  if (upErr) { console.error("[attachments.upload]", upErr); throw new Error("Could not upload the file."); }

  const { data, error } = await supabase
    .from("incentive_attachments")
    .insert({
      account_id: acct,
      program_id: programId,
      goal_id: goalId,
      storage_path: path,
      file_name: file.name.slice(0, 200),
      file_type: file.type,
      file_size: file.size,
      kind,
    })
    .select("*").single();
  if (error) {
    console.error("[attachments.catalog]", error);
    // Best-effort cleanup of the orphaned object (log if even that fails).
    const { error: rmErr } = await supabase.storage.from("incentive-attachments").remove([path]);
    if (rmErr) console.error("[attachments.catalog.cleanup]", rmErr);
    throw new Error("Could not save the attachment.");
  }
  return data;
}

export async function listAttachments(programId) {
  const acct = await accountId();
  const { data, error } = await supabase
    .from("incentive_attachments")
    .select("*").eq("account_id", acct).eq("program_id", programId)
    .order("created_at", { ascending: true });
  if (error) { console.error("[attachments.list]", error); throw new Error("Could not load attachments."); }
  return data ?? [];
}

export async function attachmentSignedUrl(path) {
  const { data, error } = await supabase.storage
    .from("incentive-attachments").createSignedUrl(path, 3600);
  if (error) { console.error("[attachments.signedUrl]", error); return null; }
  return data?.signedUrl ?? null;
}

export async function deleteAttachment(att) {
  const acct = await accountId();
  // Remove the storage OBJECT first so a failure can't orphan it (the catalog
  // row is the only record of the path); then delete the row.
  const { error: rmErr } = await supabase.storage.from("incentive-attachments").remove([att.storage_path]);
  if (rmErr) { console.error("[attachments.delete.object]", rmErr); throw new Error("Could not delete the attachment file."); }
  const { error } = await supabase
    .from("incentive_attachments").delete().eq("account_id", acct).eq("id", att.id);
  if (error) { console.error("[attachments.delete.row]", error); throw new Error("Could not delete the attachment."); }
}
